import React, { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { getBrandGuide, updateBrandGuide, uploadBrandAsset, getBrandSchemaPreview, BrandGuide, apiClient } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { Pencil, Save, X, FileDown, Sparkles, Loader2, Check, RefreshCw } from 'lucide-react';
import MockupEditor from './MockupEditor';
import { ensureGoogleFont } from '../../utils/googleFonts';
import { GlassSelect } from '../ui/GlassSelect';
import { GOOGLE_FONT_OPTIONS } from '../../data/googleFontOptions';
import './BrandPortal.css';

/** Normalize FastAPI error detail — may be a string or a Pydantic validation error array */
const getApiError = (e: any, fallback: string): string => {
  const detail = e?.response?.data?.detail;
  if (!detail) return fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map((d: any) => d?.msg ?? JSON.stringify(d)).join('; ');
  }
  return fallback;
};

// --- Helpers ---
const hexToRgb = (hex: string): { r: number; g: number; b: number } | null => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16)
  } : null;
};

/** Returns true if a hex color is perceptually light (luminance > 0.5) */
const isLightColor = (hex: string): boolean => {
  const rgb = hexToRgb(hex);
  if (!rgb) return false;
  return (0.299 * rgb.r + 0.587 * rgb.g + 0.114 * rgb.b) / 255 > 0.5;
};

const rgbToCmyk = (r: number, g: number, b: number): { c: number; m: number; y: number; k: number } => {
  let c = 1 - (r / 255);
  let m = 1 - (g / 255);
  let y = 1 - (b / 255);
  let k = Math.min(c, m, y);

  if (k === 1) {
    c = m = y = 0;
  } else {
    c = Math.round(((c - k) / (1 - k)) * 100);
    m = Math.round(((m - k) / (1 - k)) * 100);
    y = Math.round(((y - k) / (1 - k)) * 100);
    k = Math.round(k * 100);
  }

  return { c, m, y, k };
};

const BrandPortal: React.FC = () => {
  const { brandGuideId } = useParams<{ brandGuideId: string }>();
  const { isLoading: authLoading } = useAuth();
  const [brandGuide, setBrandGuide] = useState<BrandGuide | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Edit Mode State
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [editedGuide, setEditedGuide] = useState<Partial<BrandGuide>>({});
  const [saving, setSaving] = useState<boolean>(false);

  // Logo generation state
  interface LogoPreview {
    preview_id: string;
    variants: { primary: string; dark_bg: string; mono_black: string; mono_white: string; favicon: string | null };
    primary_color: string;
  }
  const [logoGenLoading, setLogoGenLoading] = useState(false);
  const [logoGenPreview, setLogoGenPreview] = useState<LogoPreview | null>(null);
  const [logoGenConfirming, setLogoGenConfirming] = useState(false);
  const [logoGenError, setLogoGenError] = useState<string | null>(null);

  // Organization JSON-LD for view-source schema visibility
  const [brandSchemaHtml, setBrandSchemaHtml] = useState<string>('');

  useEffect(() => {
    // Wait for auth to initialise so the axios request interceptor is active
    // before sending the API request. Without this, on a fresh page load the
    // brand guide fetch fires before AuthProvider sets up the interceptors,
    // meaning the Authorization header is never attached and the request fails
    // with 401 before any retry logic is in place.
    if (authLoading) return;

    const fetchBrandGuide = async () => {
      if (!brandGuideId) return;
      try {
        setLoading(true);
        const data = await getBrandGuide(brandGuideId);
        setBrandGuide(data);
        setEditedGuide(data);
        setError(null);
      } catch (err) {
        console.error('Error fetching brand guide:', err);
        setError('Failed to load brand guide.');
      } finally {
        setLoading(false);
      }
    };

    fetchBrandGuide();
  }, [brandGuideId, authLoading]);

  // Fetch Organization JSON-LD for view-source schema visibility.
  // Non-fatal — errors are silently swallowed.
  useEffect(() => {
    if (!brandGuideId || authLoading) return;
    getBrandSchemaPreview(brandGuideId)
      .then(data => { if (data.html) setBrandSchemaHtml(data.html); })
      .catch(() => {});
  }, [brandGuideId, authLoading]);

  // Load custom font files via the Font Loading API whenever brandGuide changes
  useEffect(() => {
    if (!brandGuide?.custom_fonts?.length) return;
    brandGuide.custom_fonts.forEach((f: any) => {
      const familyName = f.role === 'primary' ? (brandGuide.primary_font || f.name)
                       : f.role === 'secondary' ? (brandGuide.secondary_font || f.name)
                       : f.name;
      const font = new FontFace(familyName, `url(${f.url})`, { weight: '400', style: 'normal' });
      font.load().then(loaded => document.fonts.add(loaded)).catch(() => {});
    });
  }, [brandGuide]);

  // Live-load named brand fonts from Google Fonts so the typography preview
  // renders the real typeface (e.g. Merriweather/Lora) instead of falling back
  // to a system font. Tracks edited values so the preview updates while typing.
  // Roles backed by a custom uploaded font are skipped — those load via the
  // FontFace effect above and shouldn't be re-fetched from Google.
  useEffect(() => {
    const roles = new Set((brandGuide?.custom_fonts || []).map((f: any) => f.role));
    ensureGoogleFont(
      roles.has('primary') ? undefined : editedGuide.primary_font,
      roles.has('secondary') ? undefined : editedGuide.secondary_font,
    );
  }, [editedGuide.primary_font, editedGuide.secondary_font, brandGuide]);

  const handleEditToggle = () => {
    if (isEditing) {
      // Cancel edits
      setEditedGuide(brandGuide || {});
      setIsEditing(false);
    } else {
      setIsEditing(true);
    }
  };

  const handleSave = async () => {
    if (!brandGuideId) return;
    try {
      setSaving(true);
      const updated = await updateBrandGuide(brandGuideId, editedGuide);
      setBrandGuide(updated);
      setEditedGuide(updated);
      setIsEditing(false);
    } catch (err) {
      console.error('Error updating brand guide:', err);
      alert('Failed to save changes.');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field: keyof BrandGuide, value: any) => {
    setEditedGuide(prev => ({ ...prev, [field]: value }));
  };

  const handleImageUpload = async (file: File): Promise<{ url: string; background_preference: string } | null> => {
    try {
      const response = await uploadBrandAsset(file);
      // Use URL as-is — /assets/ is served at server root, not under /api/
      const fullUrl = response.url;
      return {
        url: fullUrl,
        background_preference: response.background_preference || 'auto'
      };
    } catch (err: any) {
      console.error('Upload failed:', err);
      alert(`Image upload failed: ${getApiError(err, err?.message || 'Unknown error')}`);
      return null;
    }
  };

  const handleGenerateLogo = async () => {
    if (!brandGuideId) return;
    setLogoGenLoading(true);
    setLogoGenError(null);
    setLogoGenPreview(null);
    try {
      const res = await apiClient.post(`/brand-guides/${brandGuideId}/generate-logo`, {
        brand_name: editedGuide.brand_name || brandGuide?.brand_name,
        design_style: 'modern',
        complexity: 'combination',
        shape: 'geometric',
      });
      setLogoGenPreview(res.data);
    } catch (err: any) {
      setLogoGenError(getApiError(err, 'Logo generation failed. Please try again.'));
    } finally {
      setLogoGenLoading(false);
    }
  };

  const handleConfirmLogo = async () => {
    if (!brandGuideId || !logoGenPreview) return;
    setLogoGenConfirming(true);
    try {
      const res = await apiClient.post(`/brand-guides/${brandGuideId}/confirm-logo`, {
        preview_id: logoGenPreview.preview_id,
      });
      // Update editedGuide with committed URLs (strip cache-buster for display)
      handleChange('logo_url', res.data.logo_url);
      handleChange('logo_dark_bg_url', res.data.logo_dark_bg_url);
      handleChange('logo_monochrome_black_url', res.data.logo_monochrome_black_url);
      handleChange('logo_monochrome_white_url', res.data.logo_monochrome_white_url);
      if (res.data.favicon_url) handleChange('favicon_url', res.data.favicon_url);
      setLogoGenPreview(null);
      // Auto-save so the brand guide immediately reflects the new logo
      const updated = await updateBrandGuide(brandGuideId, {
        ...editedGuide,
        logo_url: res.data.logo_url,
        logo_dark_bg_url: res.data.logo_dark_bg_url,
        logo_monochrome_black_url: res.data.logo_monochrome_black_url,
        logo_monochrome_white_url: res.data.logo_monochrome_white_url,
        ...(res.data.favicon_url ? { favicon_url: res.data.favicon_url } : {}),
      });
      setBrandGuide(updated);
      setEditedGuide(updated);
    } catch (err: any) {
      setLogoGenError(getApiError(err, 'Failed to commit logo.'));
    } finally {
      setLogoGenConfirming(false);
    }
  };

  if (loading) return <div className="brand-portal-loading">Loading Brand Portal...</div>;
  if (error) return <div className="brand-portal-error">{error}</div>;
  if (!brandGuide) return <div className="brand-portal-error">Brand Guide not found.</div>;

  return (
    <div className="brand-portal-container">
      {/* Organization JSON-LD — invisible, for view-source schema visibility */}
      {brandSchemaHtml && (
        <div style={{ display: 'none' }} dangerouslySetInnerHTML={{ __html: brandSchemaHtml }} />
      )}
      {/* Floating Toolbar */}
      <div className="portal-toolbar no-print">
        {isEditing ? (
          <>
            <button className="btn-save" onClick={handleSave} disabled={saving}>
              <Save size={15} style={{ marginRight: 6, verticalAlign: 'middle' }} />
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
            <button className="btn-cancel" onClick={handleEditToggle} disabled={saving}>
              <X size={15} style={{ marginRight: 4, verticalAlign: 'middle' }} />
              Cancel
            </button>
          </>
        ) : (
          <>
            <button className="btn-edit" onClick={handleEditToggle}>
              <Pencil size={15} style={{ marginRight: 6, verticalAlign: 'middle' }} />
              Edit Guide
            </button>
            <button
              className="btn-export-pdf"
              onClick={() => window.print()}
            >
              <FileDown size={15} style={{ marginRight: 6, verticalAlign: 'middle' }} />
              Print Guide
            </button>
          </>
        )}
      </div>

      {/* 1. Cover Page */}
      {(() => {
        const primaryColor = editedGuide.primary_color || '#2c3e50';
        const coverTextColor = isLightColor(primaryColor)
          ? (editedGuide.secondary_color || '#1a1a1a')
          : 'white';
        return (
      <PageWrapper className="cover-page" style={{ backgroundColor: primaryColor, color: coverTextColor }}>
        <div className="cover-grid">
          <div className="cover-header">
            <span className="brand-subtitle">Brand Identity Guidelines</span>
            <span className="brand-date">{new Date().getFullYear()}</span>
          </div>
          
          <div className="cover-main">
            <EditableText 
                value={editedGuide.brand_name || ''} 
                onChange={(v) => handleChange('brand_name', v)}
                isEditing={isEditing}
                className="brand-title"
                tagName="h1"
            />
            <EditableText 
                value={editedGuide.tagline || ''} 
                onChange={(v) => handleChange('tagline', v)}
                isEditing={isEditing}
                className="brand-tagline"
                tagName="p"
            />
          </div>

          <div className="cover-footer-info">
            <div className="footer-item">
              <span className="label">Prepared For</span>
              <span className="value">{editedGuide.brand_name}</span>
            </div>
            <div className="footer-item">
              <span className="label">Version</span>
              <span className="value">1.0</span>
            </div>
          </div>
        </div>
        
        {/* Decorative elements */}
        <div className="cover-decoration line-top"></div>
        <div className="cover-decoration line-bottom"></div>
      </PageWrapper>
        );
      })()}

      {/* 2. Introduction */}
      <PageWrapper className="intro-page">
        <div className="page-header">
          <span className="page-number">02</span>
          <h2>Introduction</h2>
        </div>
        
        <div className="intro-content">
            {/* Left Column */}
            <div className="intro-column left">
                <div className="intro-block story">
                  <h3>Our Story</h3>
                  <EditableText 
                      value={editedGuide.brand_story || 'Add your brand story here...'} 
                      onChange={(v) => handleChange('brand_story', v)}
                      isEditing={isEditing}
                      multiline
                      tagName="p"
                  />
                </div>
            </div>
          
            {/* Right Column */}
            <div className="intro-column right">
                <div className="intro-block mission">
                  <h3>Mission Statement</h3>
                  <div className="mission-box">
                    <EditableText 
                        value={editedGuide.mission_statement || 'Add your mission statement...'} 
                        onChange={(v) => handleChange('mission_statement', v)}
                        isEditing={isEditing}
                        multiline
                        tagName="p"
                    />
                  </div>
                </div>

                <div className="intro-block voice">
                  <h3>Brand Voice</h3>
                  <EditableText 
                      value={editedGuide.brand_voice || 'Describe your brand voice...'} 
                      onChange={(v) => handleChange('brand_voice', v)}
                      isEditing={isEditing}
                      multiline
                      tagName="p"
                  />
                </div>
            </div>
        </div>
      </PageWrapper>

      {/* 3. Logo Guidelines */}
      <PageWrapper className="logo-page">
        <div className="page-header">
          <span className="page-number">03</span>
          <h2>Logo Guidelines</h2>
        </div>

        {/* AI logo generation — edit mode only */}
        {isEditing && (
          <div className="logo-gen-section no-print">
            {logoGenError && (
              <div style={{ color: 'var(--danger, #ef4444)', fontSize: '0.82rem', marginBottom: '8px' }}>{logoGenError}</div>
            )}
            {logoGenPreview ? (
              <div className="logo-gen-preview-card">
                <p style={{ margin: '0 0 10px', fontWeight: 600 }}>Logo preview — does it look good?</p>
                <div className="logo-gen-variants">
                  <div className="logo-gen-cell logo-gen-light">
                    <img src={logoGenPreview.variants.primary} alt="Primary" />
                    <span>Primary</span>
                  </div>
                  <div className="logo-gen-cell logo-gen-dark">
                    <img src={logoGenPreview.variants.dark_bg} alt="Dark bg" />
                    <span>Dark bg</span>
                  </div>
                  <div className="logo-gen-cell logo-gen-light">
                    <img src={logoGenPreview.variants.mono_black} alt="Mono black" />
                    <span>Mono black</span>
                  </div>
                  <div className="logo-gen-cell logo-gen-dark">
                    <img src={logoGenPreview.variants.mono_white} alt="Mono white" />
                    <span>Mono white</span>
                  </div>
                </div>
                {editedGuide.logo_url && (
                  <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', margin: '8px 0 4px' }}>
                    ⚠ This will replace your current logos.
                  </p>
                )}
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '10px' }}>
                  <button
                    className="portal-gen-btn portal-gen-btn-primary"
                    onClick={handleConfirmLogo}
                    disabled={logoGenConfirming}
                  >
                    {logoGenConfirming ? <Loader2 size={13} style={{ animation: 'spin 1s linear infinite' }} /> : <Check size={13} />}
                    Use this logo
                  </button>
                  <button
                    className="portal-gen-btn portal-gen-btn-secondary"
                    onClick={handleGenerateLogo}
                    disabled={logoGenLoading || logoGenConfirming}
                  >
                    {logoGenLoading ? <Loader2 size={13} style={{ animation: 'spin 1s linear infinite' }} /> : <RefreshCw size={13} />}
                    Regenerate
                  </button>
                  <button
                    className="portal-gen-btn portal-gen-btn-ghost"
                    onClick={() => { setLogoGenPreview(null); setLogoGenError(null); }}
                    disabled={logoGenConfirming}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <button
                className="portal-gen-btn portal-gen-btn-secondary"
                onClick={handleGenerateLogo}
                disabled={logoGenLoading}
                title={editedGuide.logo_url ? 'Replace current logo with AI-generated one' : 'Generate a logo with AI'}
              >
                {logoGenLoading
                  ? <><Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> Generating…</>
                  : <><Sparkles size={14} /> {editedGuide.logo_url ? 'Replace with AI Logo' : 'Generate Logo with AI'}</>}
              </button>
            )}
          </div>
        )}

        <div className="logo-content-grid">
            <div className="logo-presentation" style={{
                backgroundColor: editedGuide.logo_background_preference === 'dark' ? '#1a1a1a' :
                                 editedGuide.logo_background_preference === 'light' ? '#ffffff' : 'transparent'
            }}>
                <EditableImage
                    src={editedGuide.logo_url}
                    onUpload={async (file) => {
                        const result = await handleImageUpload(file);
                        if (result) {
                            handleChange('logo_url', result.url);
                            handleChange('logo_background_preference', result.background_preference);
                        }
                    }}
                    isEditing={isEditing}
                    alt="Main Logo"
                    className="main-logo-display"
                />
            </div>

            <div className="logo-specs">
                <h3>Clear Space</h3>
                <p>Ensure the logo has enough breathing room. The clear space is defined by the height of the 'x' in the logo type.</p>

                <div className="clear-space-visual">
                    <div className="clear-space-box" style={{ backgroundColor: '#ff00b5' }}>
                        <div className="clear-space-marker top">x</div>
                        <div className="clear-space-marker right">x</div>
                        <div className="clear-space-content" style={{
                            backgroundColor: editedGuide.logo_background_preference === 'dark' ? '#1a1a1a' :
                                             editedGuide.logo_background_preference === 'light' ? '#ffffff' : '#ffffff'
                        }}>
                             {editedGuide.logo_url ? (
                                <img src={editedGuide.logo_url} alt="Logo" />
                            ) : (
                                <span className="logo-text">{editedGuide.brand_name}</span>
                            )}
                        </div>
                    </div>
                </div>
                <p className="clear-space-note" style={{ fontSize: '12px', color: '#666', marginTop: '8px' }}>
                    <span style={{ color: '#ff00b5', fontWeight: 'bold' }}>■</span> Magenta indicates the minimum exclusion zone
                </p>

                {/* Brand Icon / Favicon */}
                <div style={{ marginTop: '32px', display: 'flex', alignItems: 'center', gap: '16px' }}>
                    <div style={{ width: '64px', height: '64px', flexShrink: 0, border: '1px solid var(--stroke)', borderRadius: 'var(--radius-md)', background: 'var(--panel-strong)', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                        <EditableImage
                            src={editedGuide.favicon_url || editedGuide.logo_url}
                            onUpload={async (file) => {
                                const result = await handleImageUpload(file);
                                if (result) handleChange('favicon_url', result.url);
                            }}
                            isEditing={isEditing}
                            alt="Brand Icon / Favicon"
                            className="variation-logo"
                        />
                    </div>
                    <div>
                        <h3 style={{ margin: '0 0 4px 0' }}>Brand Icon</h3>
                        <p style={{ margin: 0, fontSize: '13px', color: 'var(--text-secondary)' }}>
                            Used as the favicon for landing pages and websites. Square format recommended (e.g. 64×64 or 512×512).
                        </p>
                    </div>
                </div>
            </div>
        </div>
      </PageWrapper>

      {/* 4. Color Palette */}
      <PageWrapper className="color-page">
        <div className="page-header">
          <span className="page-number">04</span>
          <h2>Color Palette</h2>
        </div>

        <div className="color-layout">
             {/* Primary Colors */}
             <div className="color-group primary">
                <h3>Primary Colors</h3>
                <div className="swatches-large">
                    <EditableColorSwatchLarge 
                        color={editedGuide.primary_color || '#000000'}
                        name="Primary"
                        onChange={(c) => handleChange('primary_color', c)}
                        isEditing={isEditing}
                    />
                    <EditableColorSwatchLarge 
                        color={editedGuide.secondary_color || '#ffffff'}
                        name="Secondary"
                        onChange={(c) => handleChange('secondary_color', c)}
                        isEditing={isEditing}
                    />
                </div>
             </div>

             {/* Secondary/Accent Colors */}
             <div className="color-group secondary">
                <h3>Accent & Neutral Colors</h3>
                <div className="swatches-grid">
                    <EditableColorSwatchSmall 
                        color={editedGuide.accent_color || '#cccccc'}
                        name="Accent"
                        onChange={(c) => handleChange('accent_color', c)}
                        isEditing={isEditing}
                    />
                    {/* Editable Neutral Colors */}
                    {(editedGuide.neutral_colors || ['#f5f5f5', '#e0e0e0', '#333333']).map((color, i) => (
                        <EditableColorSwatchSmall 
                            key={i} 
                            color={color} 
                            name={`Neutral ${i+1}`}
                            onChange={(c) => {
                                const newNeutrals = [...(editedGuide.neutral_colors || ['#f5f5f5', '#e0e0e0', '#333333'])];
                                newNeutrals[i] = c;
                                handleChange('neutral_colors', newNeutrals);
                            }}
                            isEditing={isEditing} 
                        />
                    ))}
                </div>
             </div>
        </div>
      </PageWrapper>

      {/* 5. Typography */}
      <PageWrapper className="type-page">
          <div className="page-header">
              <span className="page-number">05</span>
              <h2>Typography</h2>
          </div>

          <div className="type-layout">
              <div className="type-block primary">
                  <div className="font-meta">
                      <span className="font-label">Primary Typeface</span>
                      <FontPicker
                          value={editedGuide.primary_font || 'Inter'}
                          onChange={(v) => handleChange('primary_font', v)}
                          isEditing={isEditing}
                      />
                      <p className="font-usage">Used for headlines and titles.</p>
                  </div>
                  <div className="font-poster" style={{ fontFamily: editedGuide.primary_font }}>
                      <div className="poster-large">Aa</div>
                      <div className="poster-details">
                          <p className="poster-alphabet">ABCDEFGHIJKLMNOPQRSTUVWXYZ</p>
                          <p className="poster-alphabet lowercase">abcdefghijklmnopqrstuvwxyz</p>
                          <p className="poster-numbers">1234567890</p>
                      </div>
                  </div>
              </div>

              <div className="type-block secondary">
                  <div className="font-meta">
                      <span className="font-label">Secondary Typeface</span>
                      <FontPicker
                          value={editedGuide.secondary_font || 'Inter'}
                          onChange={(v) => handleChange('secondary_font', v)}
                          isEditing={isEditing}
                      />
                      <p className="font-usage">Used for body text and ui elements.</p>
                  </div>
                  <div className="font-poster" style={{ fontFamily: editedGuide.secondary_font }}>
                        <div className="poster-large">Aa</div>
                        <div className="poster-details">
                          <p className="poster-alphabet">ABCDEFGHIJKLMNOPQRSTUVWXYZ</p>
                          <p className="poster-alphabet lowercase">abcdefghijklmnopqrstuvwxyz</p>
                          <p className="poster-numbers">1234567890</p>
                      </div>
                  </div>
              </div>
          </div>
      </PageWrapper>

      {/* 6. Logo Variations */}
      <PageWrapper className="variations-page">
        <div className="page-header">
          <span className="page-number">06</span>
          <h2>Logo Variations</h2>
        </div>

        <div className="variations-grid">
            {/* Full Color - Light Background */}
            <div className="variation-card light">
                <span className="variation-label">Full Color</span>
                <div className="variation-content" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <EditableImage
                        src={editedGuide.logo_url}
                        onUpload={async (file) => {
                            const result = await handleImageUpload(file);
                            if (result) {
                                handleChange('logo_url', result.url);
                                handleChange('logo_background_preference', result.background_preference);
                            }
                        }}
                        isEditing={isEditing}
                        alt="Full Color Logo"
                        className="variation-logo"
                    />
                </div>
            </div>

            {/* Full Color - Dark Background */}
            <div className="variation-card dark" style={{ backgroundColor: '#1a1a1a' }}>
                <span className="variation-label light-text">Full Color (Dark Background)</span>
                <div className="variation-content" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <EditableImage
                        src={editedGuide.logo_dark_bg_url || editedGuide.logo_url}
                        onUpload={async (file) => {
                            const result = await handleImageUpload(file);
                            if (result) handleChange('logo_dark_bg_url', result.url);
                        }}
                        isEditing={isEditing}
                        alt="Logo for Dark Background"
                        className="variation-logo"
                    />
                </div>
            </div>

            {/* One Color - Black */}
            <div className="variation-card light">
                <span className="variation-label">One Color (Black)</span>
                <div className="variation-content" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <EditableImage
                        src={editedGuide.logo_monochrome_black_url || editedGuide.logo_url}
                        onUpload={async (file) => {
                            const result = await handleImageUpload(file);
                            if (result) handleChange('logo_monochrome_black_url', result.url);
                        }}
                        isEditing={isEditing}
                        alt="Black Monochrome Logo"
                        className={`variation-logo ${!editedGuide.logo_monochrome_black_url ? 'monochrome-black' : ''}`}
                    />
                </div>
            </div>

            {/* One Color - White/Reverse */}
            <div className="variation-card dark" style={{ backgroundColor: editedGuide.primary_color || '#3498db' }}>
                <span className="variation-label light-text">One Color (Reverse)</span>
                <div className="variation-content" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <EditableImage
                        src={editedGuide.logo_monochrome_white_url || editedGuide.logo_url}
                        onUpload={async (file) => {
                            const result = await handleImageUpload(file);
                            if (result) handleChange('logo_monochrome_white_url', result.url);
                        }}
                        isEditing={isEditing}
                        alt="White Monochrome Logo"
                        className={`variation-logo ${!editedGuide.logo_monochrome_white_url ? 'monochrome-white' : ''}`}
                    />
                </div>
            </div>

            {/* Logo Mark - icon/symbol only, no wordmark. Deliberately no fallback to
                logo_url — an absent mark must render genuinely empty, since the branding-push
                feature reads this field specifically to decide whether to fall back to AI. */}
            <div className="variation-card light">
                <span className="variation-label">Logo Mark (icon only)</span>
                <div className="variation-content" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <EditableImage
                        src={editedGuide.logo_mark_url}
                        onUpload={async (file) => {
                            const result = await handleImageUpload(file);
                            if (result) {
                                handleChange('logo_mark_url', result.url);
                                handleChange('logo_mark_background_preference', result.background_preference);
                            }
                        }}
                        isEditing={isEditing}
                        alt="Logo Mark"
                        className="variation-logo"
                    />
                </div>
            </div>

        </div>
      </PageWrapper>

      {/* 7. Stationery Mockups */}
      <PageWrapper className="stationery-page">
        <div className="page-header">
          <span className="page-number">07</span>
          <h2>Stationery</h2>
        </div>

        <div className="stationery-layout">
            <div className="business-card-group">
                <h3>Business Card</h3>
                <div className="cards-display">
                    {/* Front */}
                    <div className="biz-card front">
                        <div className="card-logo">
                            {editedGuide.logo_url ? <img src={editedGuide.logo_url} alt="Logo" /> : <span style={{fontWeight:'bold', color: editedGuide.primary_color}}>{editedGuide.brand_name}</span>}
                        </div>
                    </div>
                    {/* Back */}
                    <div className="biz-card back" style={{ backgroundColor: editedGuide.primary_color || '#333', color: isLightColor(editedGuide.primary_color || '#333') ? (editedGuide.secondary_color || '#1a1a1a') : 'white' }}>
                        <div className="card-info">
                            <span className="name">John Doe</span>
                            <span className="role">Founder & CEO</span>
                            <div className="contact-lines">
                                <span>+1 (555) 123-4567</span>
                                <span>john@{editedGuide.brand_name?.toLowerCase().replace(/\s+/g, '') || 'brand'}.com</span>
                                <span>www.{editedGuide.brand_name?.toLowerCase().replace(/\s+/g, '') || 'brand'}.com</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="letterhead-group">
                <h3>Letterhead</h3>
                <div className="letterhead-preview">
                    <div className="lh-header">
                        {editedGuide.logo_url ? <img src={editedGuide.logo_url} alt="Logo" style={{height: '40px'}} /> : <strong>{editedGuide.brand_name}</strong>}
                    </div>
                    <div className="lh-body">
                        <div className="dummy-text line-1"></div>
                        <div className="dummy-text line-2"></div>
                        <div className="dummy-text line-3"></div>
                        <div className="dummy-text line-4"></div>
                        <div className="dummy-text paragraph"></div>
                        <div className="dummy-text paragraph"></div>
                    </div>
                    <div className="lh-footer" style={{ borderTop: `2px solid ${editedGuide.primary_color || '#ccc'}` }}>
                        <span>123 Business Rd, Innovation City, Tech State 90210</span>
                        <span>www.{editedGuide.brand_name?.toLowerCase().replace(/\s+/g, '') || 'brand'}.com</span>
                    </div>
                </div>
            </div>
        </div>
      </PageWrapper>

       {/* 8. Applications */}
       <PageWrapper className="app-page" style={{ overflow: isEditing ? 'visible' : undefined }}>
        <div className="page-header">
          <span className="page-number">08</span>
          <h2>Applications</h2>
        </div>

        <div className="app-grid-expanded realistic-mockups">
            {/* Mobile Phone Mockup */}
            <div className="mockup-container photo-mockup">
                <MockupEditor
                    mockupType="mobile"
                    baseImageUrl="/mockups/mobile-base.png"
                    logoVariants={[
                        { key: 'logo_url', label: 'Full Color', url: editedGuide.logo_url },
                        { key: 'logo_dark_bg_url', label: 'Dark Background', url: editedGuide.logo_dark_bg_url },
                        { key: 'logo_monochrome_white_url', label: 'White', url: editedGuide.logo_monochrome_white_url },
                        { key: 'logo_monochrome_black_url', label: 'Black', url: editedGuide.logo_monochrome_black_url },
                    ]}
                    defaultLogoKey="logo_monochrome_white_url"
                    label="Mobile App"
                    isEditing={isEditing}
                    position="left"
                    initialSettings={editedGuide.mockup_settings?.mobile}
                    onSettingsChange={(settings) => {
                        setEditedGuide(prev => ({
                            ...prev,
                            mockup_settings: {
                                ...prev.mockup_settings,
                                mobile: settings
                            }
                        }));
                    }}
                />
            </div>

            {/* T-Shirt Mockup */}
            <div className="mockup-container photo-mockup">
                <MockupEditor
                    mockupType="tshirt"
                    baseImageUrl="/mockups/tshirt-base.png"
                    logoVariants={[
                        { key: 'logo_url', label: 'Full Color', url: editedGuide.logo_url },
                        { key: 'logo_dark_bg_url', label: 'Dark Background', url: editedGuide.logo_dark_bg_url },
                        { key: 'logo_monochrome_white_url', label: 'White', url: editedGuide.logo_monochrome_white_url },
                        { key: 'logo_monochrome_black_url', label: 'Black', url: editedGuide.logo_monochrome_black_url },
                    ]}
                    defaultLogoKey="logo_monochrome_white_url"
                    label="Polo Shirt"
                    isEditing={isEditing}
                    position="right"
                    initialSettings={editedGuide.mockup_settings?.tshirt}
                    onSettingsChange={(settings) => {
                        setEditedGuide(prev => ({
                            ...prev,
                            mockup_settings: {
                                ...prev.mockup_settings,
                                tshirt: settings
                            }
                        }));
                    }}
                />
            </div>

            {/* Coffee Cup Mockup */}
            <div className="mockup-container photo-mockup">
                <MockupEditor
                    mockupType="mug"
                    baseImageUrl="/mockups/coffee-mug-base.png"
                    logoVariants={[
                        { key: 'logo_url', label: 'Full Color', url: editedGuide.logo_url },
                        { key: 'logo_dark_bg_url', label: 'Dark Background', url: editedGuide.logo_dark_bg_url },
                        { key: 'logo_monochrome_white_url', label: 'White', url: editedGuide.logo_monochrome_white_url },
                        { key: 'logo_monochrome_black_url', label: 'Black', url: editedGuide.logo_monochrome_black_url },
                    ]}
                    defaultLogoKey="logo_url"
                    label="Coffee Mug"
                    isEditing={isEditing}
                    position="left"
                    verticalAlign="bottom" // Align controls to bottom
                    initialSettings={editedGuide.mockup_settings?.mug}
                    onSettingsChange={(settings) => {
                        setEditedGuide(prev => ({
                            ...prev,
                            mockup_settings: {
                                ...prev.mockup_settings,
                                mug: settings
                            }
                        }));
                    }}
                />
            </div>

            {/* Pen Mockup */}
            <div className="mockup-container photo-mockup">
                <MockupEditor
                    mockupType="pen"
                    baseImageUrl="/mockups/pen-base.png"
                    logoVariants={[
                        { key: 'logo_url', label: 'Full Color', url: editedGuide.logo_url },
                        { key: 'logo_dark_bg_url', label: 'Dark Background', url: editedGuide.logo_dark_bg_url },
                        { key: 'logo_monochrome_white_url', label: 'White', url: editedGuide.logo_monochrome_white_url },
                        { key: 'logo_monochrome_black_url', label: 'Black', url: editedGuide.logo_monochrome_black_url },
                    ]}
                    defaultLogoKey="logo_monochrome_white_url"
                    label="Executive Pen"
                    isEditing={isEditing}
                    position="right"
                    verticalAlign="bottom" // Align controls to bottom
                    initialSettings={editedGuide.mockup_settings?.pen}
                    onSettingsChange={(settings) => {
                        setEditedGuide(prev => ({
                            ...prev,
                            mockup_settings: {
                                ...prev.mockup_settings,
                                pen: settings
                            }
                        }));
                    }}
                />
            </div>
        </div>
      </PageWrapper>
    </div>
  );
};

// --- Reusable Components ---

const PageWrapper: React.FC<{ children: React.ReactNode; className?: string; style?: React.CSSProperties }> = ({ children, className, style }) => {
    const [scale, setScale] = useState(1);
    const pageRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleResize = () => {
            if (pageRef.current) {
                const pageWidth = 1122; 
                const windowWidth = window.innerWidth;
                const padding = 80;
                
                if (windowWidth < (pageWidth + padding)) {
                    const newScale = (windowWidth - padding) / pageWidth;
                    setScale(newScale);
                } else {
                    setScale(1);
                }
            }
        };

        handleResize();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    return (
        <div 
            className="page-scale-wrapper" 
            style={{ 
                height: scale === 1 ? 'auto' : `${210 * 3.78 * scale}px`, 
                marginBottom: '40px' 
            }}
        >
            <section 
                ref={pageRef} 
                className={`brand-page ${className || ''}`} 
                style={{ 
                    ...style,
                    transform: `scale(${scale})`,
                    transformOrigin: 'top center',
                    marginBottom: 0 
                }}
            >
                {children}
            </section>
        </div>
    );
};

// Editable Components
interface EditableProps {
    isEditing: boolean;
}

const EditableText: React.FC<EditableProps & { 
    value: string; 
    onChange: (v: string) => void; 
    className?: string; 
    tagName?: 'h1' | 'h2' | 'h3' | 'p' | 'span';
    multiline?: boolean;
}> = ({ value, onChange, isEditing, className, tagName = 'span', multiline }) => {
    if (isEditing) {
        if (multiline) {
            return (
                <textarea 
                    className={`editable-input multiline ${className || ''}`}
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                />
            );
        }
        return (
            <input 
                className={`editable-input ${className || ''}`}
                value={value}
                onChange={(e) => onChange(e.target.value)}
            />
        );
    }
    const Tag = tagName as any;
    return <Tag className={className}>{value}</Tag>;
};

/**
 * Brand-font field: a searchable Google Fonts picker when editing (exact family
 * names → no typos), a plain heading when viewing. `allowCustom` keeps a free-text
 * escape hatch for non-Google / custom-uploaded fonts.
 */
const FontPicker: React.FC<{
    value: string;
    onChange: (v: string) => void;
    isEditing: boolean;
}> = ({ value, onChange, isEditing }) => {
    if (!isEditing) return <h3 className="font-name">{value}</h3>;
    return (
        <GlassSelect
            options={GOOGLE_FONT_OPTIONS}
            value={value}
            onChange={onChange}
            searchable
            allowCustom
            size="sm"
            placeholder="Search Google Fonts…"
            className="font-name-select"
        />
    );
};

const EditableImage: React.FC<EditableProps & {
    src?: string;
    onUpload: (file: File) => void;
    alt: string;
    className?: string;
}> = ({ src, onUpload, isEditing, alt, className }) => {
    const fileInputRef = useRef<HTMLInputElement>(null);

    const handleClick = () => {
        if (isEditing && fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    return (
        <div 
            className={`editable-image-container ${isEditing ? 'editing' : ''} ${className || ''}`}
            onClick={handleClick}
        >
            {src ? (
                <img src={src} alt={alt} />
            ) : (
                <div className="image-placeholder">
                    <span>{isEditing ? 'Click to upload logo' : alt}</span>
                </div>
            )}
            {isEditing && (
                <div className="upload-overlay">
                    <span>📷 Upload</span>
                    <input 
                        type="file" 
                        ref={fileInputRef} 
                        style={{ display: 'none' }}
                        accept="image/*,.svg,image/svg+xml"
                        onChange={(e) => {
                            if (e.target.files?.[0]) onUpload(e.target.files[0]);
                        }}
                    />
                </div>
            )}
        </div>
    );
};

const EditableColorSwatchLarge: React.FC<EditableProps & {
    color: string;
    name: string;
    onChange: (c: string) => void;
}> = ({ color, name, onChange, isEditing }) => {
    const rgb = hexToRgb(color);
    const cmyk = rgb ? rgbToCmyk(rgb.r, rgb.g, rgb.b) : null;

    return (
        <div className="swatch-large-card">
            <div className="swatch-visual" style={{ backgroundColor: color }}>
                {isEditing && (
                    <input
                        type="color"
                        className="color-picker-input"
                        value={color}
                        onChange={(e) => onChange(e.target.value)}
                    />
                )}
            </div>
            <div className="swatch-info">
                <h4>{name}</h4>
                <div className="color-codes">
                    <div className="code-row">
                        <strong>HEX</strong>
                        {isEditing ? (
                            <input
                                type="text"
                                className="hex-input"
                                value={color}
                                onChange={(e) => onChange(e.target.value)}
                                style={{
                                    border: '1px solid #ddd',
                                    borderRadius: '4px',
                                    padding: '2px 6px',
                                    fontSize: '12px',
                                    width: '80px',
                                    fontFamily: 'monospace',
                                    textTransform: 'uppercase'
                                }}
                            />
                        ) : (
                            <span>{color.toUpperCase()}</span>
                        )}
                    </div>
                    {rgb && <div className="code-row"><strong>RGB</strong> <span>{rgb.r}, {rgb.g}, {rgb.b}</span></div>}
                    {cmyk && <div className="code-row"><strong>CMYK</strong> <span>{cmyk.c}, {cmyk.m}, {cmyk.y}, {cmyk.k}</span></div>}
                </div>
            </div>
        </div>
    );
};

const EditableColorSwatchSmall: React.FC<EditableProps & {
    color: string;
    name: string;
    onChange: (c: string) => void;
}> = ({ color, name, onChange, isEditing }) => {
    return (
        <div className="swatch-small-card">
            <div className="swatch-visual-small" style={{ backgroundColor: color }}>
                {isEditing && (
                    <input
                        type="color"
                        className="color-picker-input"
                        value={color}
                        onChange={(e) => onChange(e.target.value)}
                    />
                )}
            </div>
            <div className="swatch-info-small">
                <h5>{name}</h5>
                {isEditing ? (
                    <input
                        type="text"
                        className="hex-input"
                        value={color}
                        onChange={(e) => onChange(e.target.value)}
                        style={{
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            padding: '2px 4px',
                            fontSize: '10px',
                            width: '100%',
                            fontFamily: 'monospace',
                            textTransform: 'uppercase'
                        }}
                    />
                ) : (
                    <span className="hex-code">{color.toUpperCase()}</span>
                )}
            </div>
        </div>
    );
};

export default BrandPortal;